import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Car, DollarSign, TrendingUp, TrendingDown, Info } from "lucide-react";
import { VehicleMetrics } from "@/hooks/useVehicleMetrics";

interface VehicleCardProps {
  vehicle: VehicleMetrics;
}

export const VehicleCard = ({ vehicle }: VehicleCardProps) => {
  const isProfitable = vehicle.avgMonthlyProfit >= 0;
  const isOperationsVehicle = vehicle.vehicleName.toLowerCase() === "operations";
  
  // Generate motivational message for non-Operations vehicles
  const getMotivationalMessage = () => {
    if (isOperationsVehicle) return null;
    
    const partA = vehicle.profitabilityPercent < 10 
      ? "You're off to a good start!" 
      : "Keep up the good work!";
    
    const partB = vehicle.avgMonthlyProfit < 0
      ? "We'll work with you to improve your monthly profit."
      : "You're making money every month!";
    
    return `${partA} ${partB}`;
  };
  
  const motivationalMessage = getMotivationalMessage();
  
  return (
    <Card className="border-border/50 bg-gradient-to-br from-card to-card/80 backdrop-blur-sm hover:shadow-lg transition-shadow overflow-hidden">
      <CardContent className="p-0">
        <div className="flex flex-row">
          {/* Compact Image Section */}
          <div className="relative w-28 md:w-36 shrink-0 bg-muted flex items-center justify-center overflow-hidden">
            {vehicle.vehicleImage ? (
              <img 
                src={vehicle.vehicleImage} 
                alt={vehicle.vehicleName}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="flex flex-col items-center justify-center text-muted-foreground">
                <Car className="h-10 w-10 mb-1" />
                <span className="text-xs">No image</span>
              </div>
            )}
            {/* Overlay with vehicle name */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
              <h3 className="text-xs md:text-sm font-bold text-white truncate">
                {vehicle.vehicleName}
              </h3>
            </div>
          </div>

          {/* Metrics Section */}
          <div className="flex-1 p-4 space-y-3 min-w-0">
            {/* Top Metrics Row */}
            <div className="flex flex-wrap items-start gap-x-6 gap-y-2">
              <div className="space-y-0.5">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                  <DollarSign className="h-3 w-3" />
                  Total Earnings
                </p>
                <p className="text-lg font-bold text-success">
                  ${vehicle.totalEarnings.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <div className="space-y-0.5">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                  <DollarSign className="h-3 w-3" />
                  Total Expenses
                </p>
                <p className="text-lg font-bold text-destructive">
                  ${vehicle.totalExpenses.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <div className="space-y-0.5">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                  {isProfitable ? (
                    <TrendingUp className="h-3 w-3 text-success" />
                  ) : (
                    <TrendingDown className="h-3 w-3 text-destructive" />
                  )}
                  Avg Monthly Profit
                  <TooltipProvider delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs normal-case text-xs font-normal">
                        <p>Calculated from the first month this vehicle earned revenue. Excludes initial expenses such as "Car Purchase" and "Initial Repairs" to reflect ongoing operational performance.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </p>
                <p className={`text-lg font-bold ${isProfitable ? 'text-success' : 'text-destructive'}`}>
                  {isProfitable ? '+' : '-'}${Math.abs(vehicle.avgMonthlyProfit).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  {vehicle.firstEarningDate && (
                    <span className="text-[10px] font-normal text-muted-foreground ml-1.5">
                      since {vehicle.firstEarningDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                    </span>
                  )}
                </p>
              </div>
            </div>

            {/* Profitability Progress */}
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide">
                  Progress to Profitability
                </p>
                <span className="text-xs font-medium text-foreground">
                  {vehicle.profitabilityPercent}%
                </span>
              </div>
              <Progress 
                value={vehicle.profitabilityPercent} 
                className="h-2"
              />
            </div>

            {/* Motivational message */}
            {motivationalMessage && (
              <p className="text-xs text-muted-foreground italic">
                {motivationalMessage}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
